/**
 * Install-prompt state.
 *
 * Browsers differ in what they allow:
 *  - Chromium fires `beforeinstallprompt`, which can be deferred and replayed
 *    from a button, giving a real native install dialog.
 *  - Safari has no such event. Installing is manual (Share → Add to Home
 *    Screen), so all we can do is show instructions.
 *  - Firefox has no install prompt on desktop at all.
 *
 * Frequency follows the requested rule: Safari sees the banner once ever,
 * because there is no prompt to trigger and repeating an instruction they
 * have already declined is nagging. Everywhere else it shows until the app
 * is actually installed, since there is a real action to take.
 */

const DISMISSED_KEY = 'astrobot.install.dismissed';
const SAFARI_SHOWN_KEY = 'astrobot.install.safariShown';

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Storage unavailable: the banner may reappear, which is preferable to
    // crashing on a private-browsing window.
  }
}

/** Running as an installed app rather than in a browser tab. */
export function isInstalled() {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  } catch (e) {
    return false;
  }
}

/** Safari proper — not Chrome or Edge, which also carry "Safari" in the UA. */
export function isSafari() {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|SamsungBrowser/.test(ua);
}

export function isIos() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Whether to show the banner now.
 * @param {boolean} hasPrompt a deferred beforeinstallprompt is available
 */
export function shouldShowBanner(hasPrompt) {
  if (isInstalled()) return false;

  // Safari: instructions only, and only once ever.
  if (isSafari() || isIos()) {
    return !safeGet(SAFARI_SHOWN_KEY);
  }

  // Elsewhere: only worth showing when we can actually trigger an install,
  // and only until they dismiss it or install.
  if (!hasPrompt) return false;
  return !safeGet(DISMISSED_KEY);
}

/** Record that the banner has been shown, per the rules above. */
export function markShown() {
  if (isSafari() || isIos()) safeSet(SAFARI_SHOWN_KEY, 'true');
}

/** Dismissed by the user. On Safari this is the same as having been shown. */
export function markDismissed() {
  safeSet(DISMISSED_KEY, 'true');
  markShown();
}

/** Installed: stop offering it anywhere. */
export function markInstalled() {
  safeSet(DISMISSED_KEY, 'true');
  safeSet(SAFARI_SHOWN_KEY, 'true');
}

/** Register the service worker. Silent on failure — the app works without it. */
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  const register = () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  };

  // React usually mounts after `load` has already fired, so waiting for the
  // event would mean never registering. Register immediately in that case,
  // and defer only when the page genuinely is still loading.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}
